import { lazy, Suspense, type ComponentType } from 'react';
import { createRootRoute, createRoute, createRouter, Link, Outlet, useRouterState } from '@tanstack/react-router';
import { logout } from './api/client';
import { useCan, useSession, useUser } from './auth/session';
import { BrandMark, Spinner, TestVersionBadge } from './components/ui';
import { AccountPage, LoginPage, SetupPage } from './pages/AuthPages';
import { DeviceDrafts } from './fill/DocumentLists';
import { ProjectPage } from './pages/ProjectPage';
import { ProjectList, ProjectsPage } from './pages/ProjectsPage';
import { TemplatesPage } from './pages/TemplatesPage';
import { UsersPage } from './pages/UsersPage';
import { ActivityPage } from './pages/ActivityPage';
import { ErrorBoundary } from './components/ErrorBoundary';
import { SHARE_PATH } from './share/paths';

const RELOAD_KEY = 'saledeed.reloadedForNewVersion';

/**
 * A page opened before an update asks for files that the new build renamed. Reload once
 * (per minute) to pick up the new version instead of showing "Something went wrong".
 * Returns false when it already tried, so a real error is still shown.
 */
export function reloadOnceForNewVersion(): boolean {
  const last = Number(sessionStorage.getItem(RELOAD_KEY) ?? 0);
  if (Date.now() - last < 60_000) return false;
  sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
  location.reload();
  return true;
}

// Same constraint as React.lazy.
function lazyPage<C extends ComponentType<any>>(load: () => Promise<{ default: C }>) {
  return lazy(() => load().catch((err: unknown): Promise<{ default: C }> => {
    if (reloadOnceForNewVersion()) return new Promise(() => {}); // the page is reloading; never settles
    throw err;
  }));
}

// The editor (TipTap, ProseMirror) is only for template authors; others never download it.
const TemplateEditorPage = lazyPage(() => import('./pages/TemplateEditorPage').then(m => ({ default: m.TemplateEditorPage })));
const HelpPage = lazyPage(() => import('./pages/HelpPage').then(m => ({ default: m.HelpPage })));
// Executives only: the fill page (pdf runtime loads on first preview) and the wording editor (TipTap).
const FillPage = lazyPage(() => import('./fill/FillPage').then(m => ({ default: m.FillPage })));
const WordingPage = lazyPage(() => import('./fill/WordingPage').then(m => ({ default: m.WordingPage })));
// Anyone with a share link, signed in or not.
const PublicSharePage = lazyPage(() => import('./share/PublicSharePage').then(m => ({ default: m.PublicSharePage })));

/** "Ravi Kumar" → "RK"; one name → its first letter. */
function initials(name: string) {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  return ((parts[0]?.[0] ?? '') + (parts.length > 1 ? parts[parts.length - 1]![0] : '')).toUpperCase() || '?';
}

function Header() {
  const { user, actor } = useUser();
  const canUsers = useCan('account.manage');
  const canAudit = useCan('audit.read');
  const canAuthor = useCan('template.author');
  // Executives reach templates only through their projects.
  const isExec = actor === 'executive_view' || actor === 'executive_editor';
  const nav = 'shrink-0 whitespace-nowrap rounded-md px-2.5 py-1 text-sm text-slate-300 transition-colors hover:bg-white/10 hover:text-white [&.active]:bg-white/15 [&.active]:text-white';
  return (
    <header className="app-header flex h-12 items-center gap-2 overflow-x-auto bg-slate-900 px-4 text-white shadow-sm">
      <a href="#main" className="sr-only focus:not-sr-only">Skip to content</a>
      <Link to="/" className="mr-3 flex shrink-0 items-center gap-2 font-semibold whitespace-nowrap" aria-label="Sale Deed, home">
        <BrandMark className="h-7 w-7" />
        <span className="hidden sm:inline">Sale Deed</span>
      </Link>
      <TestVersionBadge dark />
      <nav aria-label="Main" className="ml-2 flex gap-1">
        <Link to="/" className={nav} activeOptions={{ exact: true }}>Home</Link>
        <Link to="/projects" className={nav}>Projects</Link>
        {!isExec && <Link to="/templates" className={nav}>Templates</Link>}
        {canUsers && <Link to="/admin/users" className={nav}>People</Link>}
        {canAudit && <Link to="/admin/activity" className={nav}>Activity</Link>}
        <Link to="/help" className={nav}>Help</Link>
      </nav>
      <span className="grow" />
      <Link to="/account" className={`${nav} inline-flex items-center`} aria-label={user.displayName} title="Your account: change password">
        <span aria-hidden="true" className="mr-1.5 inline-flex h-6 w-6 items-center justify-center rounded-full bg-blue-700 text-xs font-semibold text-white">{initials(user.displayName)}</span>
        <span className="hidden sm:inline">{user.displayName}</span>
      </Link>
      {/* A plain button: the ghost tone's dark text would win over a light override on the dark bar. */}
      <button type="button" className={nav} onClick={() => void logout()}>Sign out</button>
    </header>
  );
}

function Root() {
  const session = useSession();
  const path = useRouterState({ select: s => s.location.pathname });
  // A share link is public: it never needs (or shows) a sign-in.
  if (path === SHARE_PATH) {
    return <ErrorBoundary what="the shared templates"><Suspense fallback={<Spinner label="Opening the link" />}><PublicSharePage /></Suspense></ErrorBoundary>;
  }
  if (session.status === 'loading') return <Spinner label="Starting" />;
  if (session.status === 'anonymous') return path === '/setup' ? <SetupPage /> : <LoginPage />;
  return (
    <>
      <Header />
      <div id="main"><ErrorBoundary key={path} what="this page"><Outlet /></ErrorBoundary></div>
    </>
  );
}

const ROLE_INTRO = {
  admin: 'Build templates, and add the managers and executives who use them.',
  manager: 'Create projects, add published templates to them, and add the executives who work on them.',
  executive_view: 'Open a project to start or continue a document. Your manager adds you to projects.',
  executive_editor: 'Open a project to start or continue a document. Your manager adds you to projects.',
} as const;

/** The order things are set up in, so a new admin or manager knows where to start. */
const STEPS: Record<'admin' | 'manager', { title: string; text: string; to: '/templates' | '/projects' | '/admin/users' | '/help' }[]> = {
  admin: [
    { title: 'Build a template', text: 'Import a .docx or a template file, mark the blanks, publish.', to: '/templates' },
    { title: 'Add people', text: 'Managers and executives get a one-time setup code.', to: '/admin/users' },
    { title: 'Make a project', text: 'Add published templates and the executives who fill them.', to: '/projects' },
  ],
  manager: [
    { title: 'Make a project', text: 'One per client or batch of work.', to: '/projects' },
    { title: 'Add templates', text: 'Published masters, or your own custom copy.', to: '/projects' },
    { title: 'Add executives', text: 'They can then start documents in it.', to: '/projects' },
  ],
};

function Home() {
  const { user, actor } = useUser();
  const steps = actor === 'admin' || actor === 'manager' ? STEPS[actor] : null;
  return (
    <div className="mx-auto flex max-w-6xl flex-col gap-6 p-4 sm:p-6">
      <section className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
        <h1 className="text-xl font-semibold">Welcome, {user.displayName}</h1>
        <p className="mt-1 text-sm text-slate-600">{ROLE_INTRO[actor]}</p>
        <p className="mt-2 text-sm text-slate-600">
          Your user code is <code className="rounded bg-slate-100 px-1.5 py-0.5 font-mono text-slate-800">{user.userCode}</code>
          {actor.startsWith('executive') && '. Give it to your manager if they cannot find you in the list.'}
        </p>
        {steps && (
          <ol className="mt-4 grid gap-3 sm:grid-cols-3" aria-label="Getting started">
            {steps.map((s, i) => (
              <li key={s.title}>
                <Link to={s.to} className="flex h-full gap-3 rounded-lg border border-slate-200 p-3 transition-colors hover:border-blue-300 hover:bg-blue-50">
                  <span aria-hidden="true" className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-blue-800 text-sm font-semibold text-white">{i + 1}</span>
                  <span>
                    <span className="block text-sm font-semibold text-slate-900">{s.title}</span>
                    <span className="block text-xs text-slate-600">{s.text}</span>
                  </span>
                </Link>
              </li>
            ))}
          </ol>
        )}
      </section>
      {actor.startsWith('executive') && <DeviceDrafts />}
      <ProjectList />
    </div>
  );
}

const rootRoute = createRootRoute({ component: Root });
const routes = [
  createRoute({ getParentRoute: () => rootRoute, path: '/', component: Home }),
  createRoute({ getParentRoute: () => rootRoute, path: '/setup', component: () => <Home /> }),
  createRoute({ getParentRoute: () => rootRoute, path: '/templates', component: TemplatesPage }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: '/templates/$id',
    component: function EditorRoute() {
      const id = useRouterState({ select: s => s.location.pathname.split('/')[2] ?? '' });
      return <Suspense fallback={<Spinner label="Loading editor" />}><TemplateEditorPage id={id} /></Suspense>;
    },
  }),
  createRoute({ getParentRoute: () => rootRoute, path: '/projects', component: ProjectsPage }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: '/projects/$id',
    component: function ProjectRoute() {
      const id = useRouterState({ select: s => s.location.pathname.split('/')[2] ?? '' });
      return <ProjectPage key={id} id={id} />;
    },
  }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: '/projects/$id/documents/$docId',
    component: function FillRoute() {
      const [, , id = '', , docId = ''] = useRouterState({ select: s => s.location.pathname.split('/') });
      return <Suspense fallback={<Spinner label="Opening document" />}><FillPage key={docId} projectId={id} docId={docId} /></Suspense>;
    },
  }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: '/projects/$id/documents/$docId/wording',
    component: function WordingRoute() {
      const [, , id = '', , docId = ''] = useRouterState({ select: s => s.location.pathname.split('/') });
      return <Suspense fallback={<Spinner label="Loading editor" />}><WordingPage key={docId} projectId={id} docId={docId} /></Suspense>;
    },
  }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: '/help',
    component: () => <Suspense fallback={<Spinner label="Loading help" />}><HelpPage /></Suspense>,
  }),
  createRoute({ getParentRoute: () => rootRoute, path: '/admin/users', component: UsersPage }),
  createRoute({ getParentRoute: () => rootRoute, path: '/admin/activity', component: ActivityPage }),
  createRoute({ getParentRoute: () => rootRoute, path: '/account', component: AccountPage }),
];

/** Route errors show what went wrong and a way on, never a blank or bare page. */
function RouteError({ error, reset }: { error: unknown; reset: () => void }) {
  const detail = error instanceof Error ? `${error.name}: ${error.message}` : String(error);
  return (
    <div role="alert" className="m-4 rounded border border-red-300 bg-red-50 p-4 text-sm text-red-950" data-testid="route-error">
      <p className="font-semibold">Something went wrong on this page.</p>
      <p className="mt-1">Your typing is saved. Press <b>Try again</b>; if it comes back, press <b>Reload the page</b>. Please send this message to the developer:</p>
      <pre className="mt-2 whitespace-pre-wrap break-words rounded bg-white p-2 font-mono text-xs">{detail}</pre>
      <div className="mt-2 flex gap-2">
        <button type="button" className="rounded border border-slate-400 bg-white px-3 py-1" onClick={reset}>Try again</button>
        <button type="button" className="rounded bg-slate-900 px-3 py-1 text-white" onClick={() => location.reload()}>Reload the page</button>
      </div>
    </div>
  );
}

export const router = createRouter({ routeTree: rootRoute.addChildren(routes), defaultErrorComponent: RouteError });

declare module '@tanstack/react-router' {
  interface Register { router: typeof router }
}
